/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { InfoMessageType } from 'components/InfoMessage'
import { ReactNode } from 'react'

type ModalPrimitive = string | number | boolean | null | undefined
type ModalFlatObject = Record<string, ModalPrimitive>
type ModalPropValue =
    | ModalPrimitive
    | ReactNode
    | (() => void)
    | ModalPrimitive[]
    | ModalFlatObject

interface ModalProps {
    isCloseEnabled?: boolean
    modalSize?: string
    closeModal?: (() => void) | null
    requireCloseConfirmation?: boolean
    [key: string]: ModalPropValue
}

interface ModalPayload {
    component: ReactNode | null
    props?: ModalProps
    toggle?: boolean
    isOpen?: boolean
}

interface SecondaryModalPayload extends ModalPayload {
    title?: string | null
}

interface UiMessage {
    message: string | ReactNode
    messageType: string
}

export interface UiState {
    blur: boolean
    headbarTitle: string | null
    modal: {
        isOpen: boolean
        component: ReactNode | null
        props: ModalProps
        toggle?: boolean
    }
    secondaryModal: {
        isOpen: boolean
        component: ReactNode | null
        props: ModalProps
        title?: string | null
        toggle?: boolean
    }
    message: UiMessage | null
    infoMessage: {
        message: string | null
        messageType: InfoMessageType
        children?: ReactNode
    } | null
    remindersContext?: {
        isVisible: boolean
        modelName?: string
        modelId?: number
    } | null
    tables?: Record<string, Record<string, boolean>>
    tableFiltersVisible?: Record<string, boolean>
    tableRows?: Record<
        string,
        {
            rowId: string
            rowOriginalId?: string | number | null
            rowIndex?: number
            timestamp: number
        }
    >
}

const initialState: UiState = {
    blur: false,
    headbarTitle: null,
    modal: {
        isOpen: false,
        component: null,
        props: {},
    },
    secondaryModal: {
        isOpen: false,
        component: null,
        props: {},
        title: null,
    },
    message: null,
    infoMessage: null,
    remindersContext: null,
    tables: {},
    tableFiltersVisible: {},
    tableRows: {},
}

const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        setBlur(state, action: PayloadAction<boolean>) {
            state.blur = action.payload
        },
        setHeadbarTitle(state, action: PayloadAction<string | null>) {
            state.headbarTitle = action.payload
        },
        openModal(state, action: PayloadAction<ModalPayload>) {
            const { component, props, toggle } = action.payload
            if (toggle && state.modal.isOpen && state.modal.component === component) {
                state.modal.isOpen = false
                state.modal.component = null
            } else {
                state.modal.isOpen = !!component
                state.modal.component = component
                state.modal.props = props || {}
            }
        },
        openSecondaryModal(state, action: PayloadAction<SecondaryModalPayload>) {
            const { component, props, title, toggle } = action.payload
            if (toggle && state.secondaryModal.isOpen && state.secondaryModal.component === component) {
                state.secondaryModal.isOpen = false
                state.secondaryModal.component = null
                state.secondaryModal.title = null
            } else {
                state.secondaryModal.isOpen = !!component
                state.secondaryModal.component = component
                state.secondaryModal.props = props || {}
                state.secondaryModal.title = title || null
            }
        },
        setMessage(state, action: PayloadAction<UiMessage | null>) {
            state.message = action.payload
        },
        setInfoMessage(state, action: PayloadAction<UiState['infoMessage']>) {
            state.infoMessage = action.payload
        },
        setRemindersContext(
            state,
            action: PayloadAction<{
                isVisible: boolean
                modelName?: string
                modelId?: number
            } | null>
        ) {
            state.remindersContext = action.payload
        },
        setTableVisibility(state, action: PayloadAction<{ tableId: string; visibility: Record<string, boolean> }>) {
            if (!state.tables) {
                state.tables = {}
            }
            state.tables[action.payload.tableId] = action.payload.visibility
        },
        setTableFiltersVisible(
            state,
            action: PayloadAction<{ tableId: string; visible: boolean }>
        ) {
            if (!state.tableFiltersVisible) {
                state.tableFiltersVisible = {}
            }
            state.tableFiltersVisible[action.payload.tableId] = action.payload.visible
        },
        setTableLastClickedRow(
            state,
            action: PayloadAction<{
                tableId: string
                rowId: string
                rowOriginalId?: string | number | null
                rowIndex?: number
            }>
        ) {
            if (!state.tableRows) {
                state.tableRows = {}
            }

            state.tableRows[action.payload.tableId] = {
                rowId: action.payload.rowId,
                rowOriginalId: action.payload.rowOriginalId,
                rowIndex: action.payload.rowIndex,
                timestamp: Date.now(),
            }
        },
        clearTableLastClickedRow(state, action: PayloadAction<{ tableId: string }>) {
            if (!state.tableRows) {
                return
            }

            delete state.tableRows[action.payload.tableId]
        },
        resetState: () => initialState,
    },
})

export const {
    setBlur,
    setHeadbarTitle,
    openModal,
    openSecondaryModal,
    setMessage,
    setInfoMessage,
    setRemindersContext,
    setTableVisibility,
    setTableFiltersVisible,
    setTableLastClickedRow,
    clearTableLastClickedRow,
    resetState,
} = uiSlice.actions

export default uiSlice.reducer
